import { Dropdown, FormField, Icon } from 'semantic-ui-react';

import { PERSON_DOCUMENT_ACCEPT, PERSON_DOCUMENT_KIND_OPTIONS } from '../../../utils/registration';
import type { PendingDocument } from '../../../hooks/usePersonDocumentUpload';
import { useRegistrationText } from './registrationText';

/**
 * Attachments collected alongside the registration form — a thumbprint signature, a parental
 * permission slip, a scan of the form itself.
 *
 * Queued here and uploaded **after** the person is created, because the presigned upload needs
 * a person id to hang the row off. That is the same sequence the photo already follows.
 */

export const SupportingDocuments = ({
    documents,
    onChange,
    disabled,
    alreadyOnFile,
}: {
    documents: PendingDocument[];
    onChange: (next: PendingDocument[]) => void;
    disabled?: boolean;
    /** How many are already on the person's profile — set when the form is editing someone. */
    alreadyOnFile?: number;
}) => {
    const t = useRegistrationText();
    const kindOptions = PERSON_DOCUMENT_KIND_OPTIONS.map((o) => ({ ...o, text: t.documentKinds[o.value] ?? o.text }));
    const update = (index: number, patch: Partial<PendingDocument>) =>
        onChange(documents.map((d, i) => (i === index ? { ...d, ...patch } : d)));

    const remove = (index: number) => onChange(documents.filter((_, i) => i !== index));

    const addFiles = (files: FileList | null) => {
        if (files == null || files.length === 0) return;
        onChange([
            ...documents,
            // Defaulting the kind rather than demanding it first: the common case is a stack of
            // permission slips, and a wrong default is one dropdown to fix.
            ...Array.from(files).map((file) => ({ kind: 'PERMISSION_SLIP', file })),
        ]);
    };

    return (
        <div className="creator-section">
            <div className="creator-section-title">{t.documentsTitle}</div>
            <p className="creator-section-hint">{t.documentsHint}</p>
            {alreadyOnFile != null && alreadyOnFile > 0 && (
                <p className="creator-section-hint">{t.documentsOnFile(alreadyOnFile)}</p>
            )}

            {documents.map((doc, index) => (
                <div className="document-row" key={`${doc.file.name}-${index}`}>
                    <Dropdown
                        selection
                        compact
                        disabled={disabled}
                        options={kindOptions}
                        value={doc.kind}
                        onChange={(_, { value }) => update(index, { kind: String(value) })}
                    />
                    <span className="document-name">{doc.file.name}</span>
                    <Icon
                        name="times circle"
                        color="red"
                        link
                        disabled={disabled}
                        onClick={() => !disabled && remove(index)}
                    />
                </div>
            ))}

            <FormField>
                <label htmlFor="person-document-input">{t.addDocument}</label>
                <input
                    id="person-document-input"
                    type="file"
                    multiple
                    disabled={disabled}
                    accept={PERSON_DOCUMENT_ACCEPT}
                    onChange={(e) => {
                        addFiles(e.target.files);
                        // Clear the input so re-picking the same file fires `change` again.
                        e.target.value = '';
                    }}
                />
            </FormField>
        </div>
    );
};
